import {registerLocaleData} from '#packages/ecma402-abstract/registerLocaleData.js'
import {ToString} from '#packages/ecma262-abstract/ToString.js'
import {CanonicalizeLocaleList} from '#packages/ecma402-abstract/CanonicalizeLocaleList.js'
import {SupportedLocales} from '#packages/ecma402-abstract/SupportedLocales.js'
import {
  type LocaleFieldsData,
  type RelativeTimeLocaleData,
} from '#packages/ecma402-abstract/types/relative-time.js'
import {InitializeRelativeTimeFormat} from '#packages/ecma402-abstract/RelativeTimeFormat/InitializeRelativeTimeFormat.js'
import {PartitionRelativeTimePattern} from '#packages/ecma402-abstract/RelativeTimeFormat/PartitionRelativeTimePattern.js'
import getInternalSlots from '#packages/intl-relativetimeformat/get_internal_slots.js'

export default class RelativeTimeFormat {
  constructor(
    locales?: string | string[],
    options?: Intl.RelativeTimeFormatOptions
  ) {
    // test262/test/intl402/RelativeTimeFormat/constructor/constructor/newtarget-undefined.js
    // Cannot use `new.target` bc of IE11 & TS transpiles it to something else
    const newTarget =
      this && this instanceof RelativeTimeFormat ? this.constructor : void 0
    if (!newTarget) {
      throw new TypeError("Intl.RelativeTimeFormat must be called with 'new'")
    }
    return InitializeRelativeTimeFormat(this, locales, options, {
      getInternalSlots,
      availableLocales: RelativeTimeFormat.availableLocales,
      relevantExtensionKeys: RelativeTimeFormat.relevantExtensionKeys,
      localeData: RelativeTimeFormat.localeData,
      getDefaultLocale: RelativeTimeFormat.getDefaultLocale,
    })
  }
  format(value: number, unit: Intl.RelativeTimeFormatUnit): string {
    if (typeof this !== 'object') {
      throw new TypeError('format was called on a non-object')
    }
    const internalSlots = getInternalSlots(this)
    if (!internalSlots.initializedRelativeTimeFormat) {
      throw new TypeError('format was called on a invalid context')
    }
    return PartitionRelativeTimePattern(
      this,
      // ECMA-402 §18.3.3 Intl.RelativeTimeFormat.prototype.format, step 3.
      // ToNumber rejects BigInt: https://tc39.es/ecma402/#sec-Intl.RelativeTimeFormat.prototype.format
      // https://github.com/tc39/ecma402/blob/b1c961988b9a07894b1dc3dc2b5626ea48387d61/spec/relativetimeformat.html#L175
      +value,
      ToString(unit) as Intl.RelativeTimeFormatUnit,
      {
        getInternalSlots,
      }
    )
      .map(el => el.value)
      .join('')
  }
  formatToParts(
    value: number,
    unit: Intl.RelativeTimeFormatUnit
  ): Intl.RelativeTimeFormatPart[] {
    if (typeof this !== 'object') {
      throw new TypeError('formatToParts was called on a non-object')
    }
    const internalSlots = getInternalSlots(this)
    if (!internalSlots.initializedRelativeTimeFormat) {
      throw new TypeError('formatToParts was called on a invalid context')
    }
    return PartitionRelativeTimePattern(
      this,
      // ECMA-402 §18.3.4 Intl.RelativeTimeFormat.prototype.formatToParts, step 3.
      // ToNumber rejects BigInt: https://tc39.es/ecma402/#sec-Intl.RelativeTimeFormat.prototype.formatToParts
      // https://github.com/tc39/ecma402/blob/b1c961988b9a07894b1dc3dc2b5626ea48387d61/spec/relativetimeformat.html#L189
      +value,
      ToString(unit) as Intl.RelativeTimeFormatUnit,
      {getInternalSlots}
    )
  }

  resolvedOptions(): Intl.ResolvedRelativeTimeFormatOptions {
    if (typeof this !== 'object') {
      throw new TypeError('resolvedOptions was called on a non-object')
    }
    const internalSlots = getInternalSlots(this)
    if (!internalSlots.initializedRelativeTimeFormat) {
      throw new TypeError('resolvedOptions was called on a invalid context')
    }

    // test262/test/intl402/RelativeTimeFormat/prototype/resolvedOptions/type.js
    return {
      locale: internalSlots.locale,
      style: internalSlots.style,
      numeric: internalSlots.numeric,
      numberingSystem: internalSlots.numberingSystem,
    }
  }

  public static supportedLocalesOf(
    locales: string | string[],
    options?: Pick<Intl.RelativeTimeFormatOptions, 'localeMatcher'>
  ): string[] {
    return SupportedLocales(
      RelativeTimeFormat.availableLocales,
      CanonicalizeLocaleList(locales),
      options
    )
  }

  public static __addLocaleData(...data: RelativeTimeLocaleData[]): void {
    for (const {data: d, locale} of data) {
      registerLocaleData(
        locale,
        d,
        RelativeTimeFormat.localeData,
        RelativeTimeFormat.availableLocales
      )
      if (!RelativeTimeFormat.__defaultLocale) {
        RelativeTimeFormat.__defaultLocale = locale
      }
    }
  }
  static localeData: Record<string, LocaleFieldsData> = {}
  private static availableLocales = new Set<string>()
  private static __defaultLocale = ''
  private static getDefaultLocale() {
    return RelativeTimeFormat.__defaultLocale
  }
  private static relevantExtensionKeys = ['nu']
  public static polyfilled = true
}

try {
  // IE11 does not have Symbol
  if (typeof Symbol !== 'undefined') {
    Object.defineProperty(RelativeTimeFormat.prototype, Symbol.toStringTag, {
      value: 'Intl.RelativeTimeFormat',
      writable: false,
      enumerable: false,
      configurable: true,
    })
  }

  // https://github.com/tc39/test262/blob/master/test/intl402/RelativeTimeFormat/constructor/length.js
  Object.defineProperty(RelativeTimeFormat.prototype.constructor, 'length', {
    value: 0,
    writable: false,
    enumerable: false,
    configurable: true,
  })
  // https://github.com/tc39/test262/blob/master/test/intl402/RelativeTimeFormat/constructor/supportedLocalesOf/length.js
  Object.defineProperty(RelativeTimeFormat.supportedLocalesOf, 'length', {
    value: 1,
    writable: false,
    enumerable: false,
    configurable: true,
  })
} catch {
  // Meta fix so we're test262-compliant, not important
}
